/// <amd-module name="Dashboards/ConfigHost/ConfigHost" />
import { ConfigHostProps } from "Dashboards/ConfigHost/ConfigHostProps";
import { UnhandledErrorMessage } from "Dashboards/ConfigHost/Resources";
import { resolveComponentContributionAsync, resolveWidgetContributionAsync } from "Dashboards/WidgetCatalog/WidgetContributionsHelper";
import { ContributionComponentProps, WidgetContributionRenderingProps } from "Dashboards/WidgetContracts/Internal/WidgetContributionTypes";
import { LoadingStatus } from "Dashboards/WidgetContracts/WidgetProps";
import * as React from "react";
import { format } from "VSS/Core/Util/String";
import { ErrorBoundary } from "VSS/Platform/Components/ErrorBoundary";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { LegacyConfigHost } from "./HostImplementations/LegacyConfigHost";
import { ModernConfigHost } from "./HostImplementations/ModernConfigHost";

enum ConfigHostMode {
    loading = 0,
    renderLegacyConfigHost = 1,
    renderModernConfigHost = 2,
    failedToLoad = 3
}

interface ConfigHostState {
    /** describes mode if config is in a terminal (render) state  */
    loadFailureMessage?: string;

    /** Render Props on the Widget Contribution. This *should* be present for all widgets. */
    contributionRenderingProps?: WidgetContributionRenderingProps;

    /** Props describing new platform render behavior on the "Satellite" Contribution. Not guaranteed to be present for all widgets. */
    configComponentContributionProps?: ContributionComponentProps;

    /** Indicates to fallback to legacy rendering if modern contribution was referenced but could not be loaded (notably with feature flagging). */
    fallbackToLegacyRendering?: boolean;
}

/** Responsible for selecting the right host for rendering config, and dealing with common concerns. */
export class ConfigHost extends VssComponent<ConfigHostProps, ConfigHostState> {
    constructor(props: ConfigHostProps, context: IVssComponentContext) {
        super(props, context);
        this.state = {};
    }

    public render(): JSX.Element | null {
        // This layer is responsible for handling selection between modern and legacy Config
        let configHost = null;
        switch (this.determineMode()) {
            case ConfigHostMode.renderLegacyConfigHost:
                configHost = <LegacyConfigHost {...this.props} />;
                break;
            case ConfigHostMode.renderModernConfigHost:
                configHost = <ModernConfigHost {...this.props} configContributionComponentProps={this.state.configComponentContributionProps!} />;
                break;
            default:
                break;
        }

        // Error Boundary is a protection of last resort, for scenarios where continued behavior of a problem configHost is untenable.
        return <ErrorBoundary handlerComponentType="tfs.page-error-handler">{configHost}</ErrorBoundary>;
    }

    public async componentDidMount(): Promise<void> {
        try {
            const contributionprops = await resolveWidgetContributionAsync(this.context.pageContext, this.props.config.contributionId);
            const newState: ConfigHostState = {
                contributionRenderingProps: contributionprops
            };
            this.setState(newState);

            if (contributionprops && contributionprops.configComponentContributionId) {
                const componentProps = await resolveComponentContributionAsync(
                    this.context.pageContext,
                    contributionprops.configComponentContributionId
                );
                if (componentProps) {
                    this.setState({
                        configComponentContributionProps: componentProps
                    });
                } else {
                    this.setState({ fallbackToLegacyRendering: true });
                }
            }
        } catch (e) {
            this.setState({ loadFailureMessage: e });
        }
    }

    /** Provides error detection for exceptions thrown on synchronous codepaths. Failures of background operations/promises do not get detected here, and must be handled by widgets themselves.  */
    public componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        this.setState({
            loadFailureMessage: error.message
        });

        this.props.parentProps.onConfigLoaded({
            outcome: LoadingStatus.failure,
            outcomeDescription: format(UnhandledErrorMessage, error.toString()),
            extendedDiagnosticDetail: error
        });
    }

    private determineMode(): ConfigHostMode {
        if (this.state.loadFailureMessage) {
            // If we are in a terminal mode, just return that without any interpretation.
            return ConfigHostMode.failedToLoad;
        } else {
            if (!this.state.contributionRenderingProps) {
                return ConfigHostMode.loading;
            } else if (this.state.contributionRenderingProps.configComponentContributionId && !this.state.fallbackToLegacyRendering) {
                if (!this.state.configComponentContributionProps) {
                    // Loading or failed to load New Plat contribution. (Need to explicitly track & render for failure possibilities)
                    return ConfigHostMode.loading;
                } else {
                    return ConfigHostMode.renderModernConfigHost;
                }
            } else {
                return ConfigHostMode.renderLegacyConfigHost;
            }
        }
    }
}
